
import { useState } from "react"
import {
  MessageSquare,
  Key,
  Bell,
  Shield,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  Brain,
  Save,
  Loader2,
  Palette,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/lib/auth-context"
import { useCanvaConnection } from "@/lib/use-api"
import { CanvaConnectionButton } from "@/components/canva/CanvaConnectionButton"

export default function SettingsPage() {
  const { user } = useAuth()
  const { data: canvaConnection } = useCanvaConnection()
  const canvaConnected = canvaConnection?.connected ?? false
  const [intercomAppId, setIntercomAppId] = useState("")
  const [intercomToken, setIntercomToken] = useState("")
  const [isConnecting, setIsConnecting] = useState(false)
  const [intercomConnected, setIntercomConnected] = useState(false)
  const [autoTag, setAutoTag] = useState(true)
  const [autoProfile, setAutoProfile] = useState(true)
  const [syncNotes, setSyncNotes] = useState(false)
  const [notifications, setNotifications] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  function handleConnectIntercom() {
    if (!intercomAppId || !intercomToken) return
    setIsConnecting(true)
    setTimeout(() => {
      setIntercomConnected(true)
      setIsConnecting(false)
    }, 1500)
  }

  function handleSave() {
    setIsSaving(true)
    setTimeout(() => {
      setIsSaving(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    }, 800)
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Configure Intercom integration, AI preferences, and notification settings.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column: Settings */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Intercom Integration */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">
                  Intercom Integration
                </h3>
              </div>
              {intercomConnected && (
                <div className="flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" />
                  Connected
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Connect your Intercom workspace to enable automatic investor detection and conversation analysis.
            </p>

            <div className="mt-4 flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Intercom App ID
                </label>
                <input
                  type="text"
                  value={intercomAppId}
                  onChange={(e) => setIntercomAppId(e.target.value)}
                  placeholder="e.g. abc123xyz"
                  disabled={intercomConnected}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                  Access Token
                </label>
                <input
                  type="password"
                  value={intercomToken}
                  onChange={(e) => setIntercomToken(e.target.value)}
                  placeholder="dG9rZW4..."
                  disabled={intercomConnected}
                  className="w-full rounded-lg border border-input bg-card px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                />
              </div>
              {!intercomConnected && (
                <button
                  type="button"
                  onClick={handleConnectIntercom}
                  disabled={isConnecting || !intercomAppId || !intercomToken}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4" />
                      Connect Intercom
                    </>
                  )}
                </button>
              )}
            </div>

            {intercomConnected && (
              <div className="mt-4 rounded-lg bg-emerald-50 p-3">
                <p className="text-xs text-emerald-700">
                  Intercom workspace connected successfully. Irene will now automatically detect investor conversations and sync data.
                </p>
              </div>
            )}
          </div>

          {/* Canva Integration */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">
                  Canva Integration
                </h3>
              </div>
              <CanvaConnectionButton />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Connect your Canva account to create and edit pitch decks directly from Irene. Import uploaded decks, personalize presentations for investors, and export as PDF or PPTX.
            </p>
            {canvaConnected && (
              <div className="mt-4 rounded-lg bg-emerald-50 p-3">
                <p className="text-xs text-emerald-700">
                  Canva account connected. You can now import decks, create presentations, and export designs from the Deck Analyzer page.
                </p>
              </div>
            )}
          </div>

          {/* Automation Settings */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">
                AI Automation
              </h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Configure how Irene processes and analyzes investor conversations.
            </p>

            <div className="mt-4 flex flex-col gap-4">
              <ToggleSetting
                label="Auto-tag Conversations"
                description="Automatically tag Intercom conversations with investor stage (Lead, Active, Portfolio)"
                enabled={autoTag}
                onToggle={() => setAutoTag(!autoTag)}
              />
              <ToggleSetting
                label="Auto-generate Profiles"
                description="Create AI-generated investor profiles from conversation data"
                enabled={autoProfile}
                onToggle={() => setAutoProfile(!autoProfile)}
              />
              <ToggleSetting
                label="Sync Notes to Intercom"
                description="Push Irene notes and insights back to Intercom contact records"
                enabled={syncNotes}
                onToggle={() => setSyncNotes(!syncNotes)}
              />
              <ToggleSetting
                label="Deal Room Notifications"
                description="Get notified via Intercom when an investor views your deal room"
                enabled={notifications}
                onToggle={() => setNotifications(!notifications)}
              />
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">
                Notification Preferences
              </h3>
            </div>
            <div className="mt-4 flex flex-col gap-3">
              {[
                "New investor conversation detected",
                "Investor views deal room",
                "Term sheet or commitment signal detected",
                "Weekly engagement summary",
                "AI-generated follow-up suggestions",
              ].map((item) => (
                <label key={item} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 rounded border-input accent-primary"
                  />
                  <span className="text-sm text-foreground">{item}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Save */}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Saved
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                Save Settings
              </>
            )}
          </button>
        </div>

        {/* Right Column: Status & Help */}
        <div className="flex flex-col gap-6">
          {/* Account Info */}
          {user && (
            <div className="glass-strong rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-foreground">Account</h3>
              <div className="mt-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Name</span>
                  <span className="text-sm font-medium text-foreground">{user.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Email</span>
                  <span className="text-sm font-medium text-foreground">{user.email}</span>
                </div>
                {user.company_name && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Company</span>
                    <span className="text-sm font-medium text-foreground">{user.company_name}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Plan</span>
                  <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary capitalize">{user.plan}</span>
                </div>
              </div>
            </div>
          )}

          {/* Connection Status */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              Integration Status
            </h3>
            <div className="mt-4 flex flex-col gap-3">
              <StatusItem
                label="Intercom"
                connected={intercomConnected}
              />
              <StatusItem label="Canva" connected={canvaConnected} />
              <StatusItem label="OpenAI (via Vercel AI Gateway)" connected={true} />
              <StatusItem label="Stripe Billing" connected={true} />
              <StatusItem label="Tavily Search" connected={true} />
              <StatusItem label="Email (SMTP)" connected={false} />
            </div>
          </div>

          {/* Security */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Security</h3>
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">
                All API keys are encrypted at rest. Conversation data is processed in real-time and not stored by the AI provider.
              </p>
              <a
                to="#"
                className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                View privacy policy <ExternalLink className="h-3 w-3" />
              </a>
              <a
                to="#"
                className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Data processing agreement <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>

          {/* Intercom Setup Guide */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              Intercom Setup Guide
            </h3>
            <ol className="mt-3 flex flex-col gap-2 text-xs text-muted-foreground list-decimal list-inside">
              <li>Go to <a to="https://app.intercom.com/a/apps/_/settings/identity-verification" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Intercom Settings</a></li>
              <li>Navigate to <strong className="text-foreground">Settings &rarr; Integrations &rarr; Developer Hub</strong></li>
              <li>Create a new app or select your existing one</li>
              <li>Copy the <strong className="text-foreground">App ID</strong> (found in your workspace URL)</li>
              <li>Generate an <strong className="text-foreground">Access Token</strong> with read/write permissions</li>
              <li>Paste both values in the form on this page</li>
            </ol>
          </div>

          {/* OpenAI Info */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              OpenAI Integration
            </h3>
            <p className="mt-2 text-xs text-muted-foreground">
              Irene uses OpenAI GPT-4o via the <strong className="text-foreground">Vercel AI Gateway</strong> -- no API key needed on your end. This powers:
            </p>
            <ul className="mt-2 flex flex-col gap-1.5 text-xs text-muted-foreground">
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-primary" /> Investor update generation</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-primary" /> Deck analysis</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-primary" /> Investor profile insights</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-primary" /> Autonomous investor search</li>
            </ul>
          </div>

          {/* Help */}
          <div className="glass-strong rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">
              Need Help?
            </h3>
            <div className="mt-3 flex flex-col gap-2">
              <a
                to="https://developers.intercom.com/docs/build-an-integration/getting-started/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-input px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
              >
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                Intercom Developer Docs
              </a>
              <a
                to="https://sdk.vercel.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-input px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
              >
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                Vercel AI SDK Docs
              </a>
              <a
                to="https://tavily.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-input px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
              >
                <ExternalLink className="h-3 w-3 text-muted-foreground" />
                Tavily Search API
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ToggleSetting({
  label,
  description,
  enabled,
  onToggle,
}: {
  label: string
  description: string
  enabled: boolean
  onToggle: () => void
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-input p-3">
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
          enabled ? "bg-primary" : "bg-muted"
        )}
      >
        <span
          className={cn(
            "inline-block h-4 w-4 rounded-full bg-card shadow-sm transition-transform",
            enabled ? "translate-x-6" : "translate-x-1"
          )}
        />
      </button>
    </div>
  )
}

function StatusItem({ label, connected }: { label: string; connected: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-foreground">{label}</span>
      <div
        className={cn(
          "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
          connected
            ? "bg-emerald-100 text-emerald-700"
            : "bg-amber-100 text-amber-700"
        )}
      >
        {connected ? (
          <CheckCircle2 className="h-3 w-3" />
        ) : (
          <AlertCircle className="h-3 w-3" />
        )}
        {connected ? "Connected" : "Not Connected"}
      </div>
    </div>
  )
}
